import { enqueueSnackbar } from 'notistack'
import pkg from '../../package.json'
import { ROOT_URL } from '../configs/config'
import { stringifyError } from '../utils/utils-functions'

type ContentTypeKey = 'json' | 'form'
type ContentTypeValue = 'application/json' | 'application/x-www-form-urlencoded'

const CONTENT_TYPES_MAP: Map<ContentTypeKey, ContentTypeValue> = new Map([
  ['json', 'application/json'],
  ['form', 'application/x-www-form-urlencoded']
])

interface MakeRequestOptions<RequestBody> {
  body?: RequestBody,
  authorized?: boolean,
  method: 'POST' | 'GET' | 'PUT' | 'DELETE',
  contentType: ContentTypeKey
}

interface GetRequestOptions<RequestBody> {
  data?: RequestBody;
  apiUrl?: string;
  authorized?: boolean;
}

interface PostRequestOptions<RequestBody> extends GetRequestOptions<RequestBody> {
  contentType?: ContentTypeKey;
}

const ApiService = {
  token: null as string | null,

  logout: null as (() => void) | null,

  makeRequest: async <Req, Res> (url: string, {
    body,
    authorized,
    method,
    contentType
  }: MakeRequestOptions<Req>) => {
    try {
      const response = await fetch(url, {
        method,
        headers: ApiService.getHeaders({ authorized, contentType }),
        body: body as BodyInit
      })
      if (!response.ok) {
        if (response.status === 401 && ApiService.logout) ApiService.logout()
        let error = await response.text()
        try {
          error = JSON.parse(error)
        } catch {
          console.warn('server didn\'t respond with json!')
        }
        if (response.status === 426) {
          enqueueSnackbar(stringifyError(error), { variant: 'error' })
        }
        throw error
      }
      return response.json() as Res
    } catch (error) {
      /*if (process.env.NODE_ENV === 'production') {
                bugsnagNotify(error);
            }*/
      console.error(error)
      throw error
    }
  },

  getHeaders: ({ authorized, contentType }: {
        authorized?: boolean;
        contentType: ContentTypeKey;
    }) => {
    const headers = new Headers()
    if (authorized) {
      headers.append('Authorization', `Bearer ${ApiService.token}`)
    }
    headers.append('X-Mta-Dashboard-Version', pkg.version)
    headers.append('Content-Type', CONTENT_TYPES_MAP.get(contentType) as ContentTypeValue)
    return headers
  },

  get: <Req, Res> (url: string, {
    data,
    apiUrl = ROOT_URL,
    authorized = true
  }: GetRequestOptions<Req>) => {
    const queryStringParams = data ? ApiService.createRequestBody(data, 'form').toString() : ''
    let urlString = `${apiUrl}${url}`
    if (queryStringParams) {
      urlString += `?${queryStringParams}`
    }
    return ApiService.makeRequest(urlString, {
      method: 'GET',
      contentType: 'form',
      authorized
    }) as Res
  },

  post: <Req, Res> (url: string, {
    data,
    contentType = 'json',
    apiUrl = ROOT_URL,
    authorized = true
  }: PostRequestOptions<Req>) => ApiService.makeRequest(`${apiUrl}${url}`, {
    method: 'POST',
    authorized,
    contentType,
    body: data ? ApiService.createRequestBody(data, contentType) : undefined
  }) as Res,

  createRequestBody: <Req> (data: Req, type: ContentTypeKey) => {
    switch (type) {
      case 'json':
        return JSON.stringify(data)
      case 'form':
        return new URLSearchParams(data as URLSearchParams)
      default:
        return data
    }
  },

  setToken: (token: string | null) => {
    ApiService.token = token
  },

  setLogout: (logout: any) => {
    ApiService.logout = logout
  }
}

export default ApiService
